import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toasts = signal<Toast[]>([]);
  private idCounter = 0;

  /**
   * Timestamp until which error() calls are suppressed.
   * Set by the ErrorHandlerInterceptor after it shows its own toast, so
   * component catchError blocks don't show a duplicate toast.
   */
  private suppressErrorUntil = 0;

  /**
   * Suppress error toasts for the given number of milliseconds.
   * Called by ErrorHandlerInterceptor immediately after it shows a toast.
   */
  suppressErrorsFor(ms: number): void {
    this.suppressErrorUntil = Date.now() + ms;
  }

  // Public getter for reactive access
  toasts$ = this.toasts.asReadonly();

  /**
   * Show a success toast
   */
  success(title: string, message: string, duration: number = 5000): void {
    this.show('success', title, message, duration);
  }

  /**
   * Show an error toast.
   * Suppressed for 300ms after ErrorHandlerInterceptor shows its own toast
   * to prevent duplicate toasts from component catchError blocks.
   */
  error(title: string, message: string, duration: number = 7000): void {
    this.show('error', title, message, duration);
  }

  /**
   * Show a warning toast
   */
  warning(title: string, message: string, duration: number = 6000): void {
    this.show('warning', title, message, duration);
  }

  /**
   * Show an info toast
   */
  info(title: string, message: string, duration: number = 5000): void {
    this.show('info', title, message, duration);
  }

  /**
   * Show a toast with custom configuration
   */
  show(
    type: Toast['type'], 
    title: string, 
    message: string, 
    duration: number = 5000,
    position: Toast['position'] = 'top-right'
  ): void {
    // Suppress error toasts during the window opened by ErrorHandlerInterceptor.
    // Guard is here (not in error()) so ALL paths — error(), show('error',...),
    // commonService.showToast('error',...) — are covered.
    if (type === 'error' && Date.now() < this.suppressErrorUntil) return;

    // Deduplicate: skip if an identical toast (same type + title + message) already exists
    const existing = this.toasts().find(
      t => t.type === type && t.title === title && t.message === message
    );
    if (existing) {
      return;
    }

    const toast: Toast = {
      id: `toast-${++this.idCounter}`,
      type,
      title,
      message,
      duration,
      position
    };

    // Add toast to the array
    this.toasts.update(toasts => [...toasts, toast]);

    // Auto-remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(toast.id);
      }, duration);
    }
  }

  /**
   * Remove a specific toast
   */
  remove(id: string): void {
    this.toasts.update(toasts => toasts.filter(toast => toast.id !== id));
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toasts.set([]);
  }

  /**
   * Get current toasts
   */
  getToasts(): Toast[] {
    return this.toasts();
  }
}
